import React, { useState, useEffect } from 'react'
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  FormHelperText,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import CircularProgress from '@mui/material/CircularProgress'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import {
  fetchPlantList,
  fetchPlantListNew,
  fetchPlantsForIssues,
  fetchUserList,
  fetchFrequentTags,
} from '../../services/issueService'
import { uploadAttachment } from '../../services/issueService'

//Hardcoded default tag suggestions
const DEFAULT_TAG_SUGGESTIONS = [
  'Maintenance',
  'Emergency',
  'Generation Loss',
  'OEM',
  'Inverter',
  'Energy Meter',
  'Datalogger',
  'Sensor',
]

const IssueForm = ({
  issue,
  onSubmit,
  onCancel,
  categoryOptions,
  statusOptions,
  priorityOptions,
  escalationOptions,
  issueTypeOptions,
  //fallback if API returns no users
  assignedToOptions = [],
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    plantId: '',
    plantName: '',
    plantCapacity: 0,
    category: '',
    issueType: '',
    priority: 'Medium',
    status: 'Open',
    assignedTo: '',
    assignedToUserId: '',
    responsibleTeamExternal: '',
    reportedBy: '',
    //assignedToName: '',
    expectedClosingDate: dayjs().add(7, 'day'),
    actualClosingDate: null,
    firstObservedDate: dayjs(),
    tags: [],
    attachments: [],
    severityImpact: 'Medium',
    affectedEquipment: '',
    escalationLevel: '',
    rootCausePreliminary: '',
    nextActionFollowUpPlan: '',
    actionTaken: '',
    customerCommunicationDone: false,
  })

  const [plants, setPlants] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [tagInput, setTagInput] = useState('')
  const [frequentTags, setFrequentTags] = useState([])

  useEffect(() => {
    loadPlants()
    loadUsers()
    loadFrequentTags()
  }, [])

  useEffect(() => {
    if (issue) {
      // Transform MongoDB data to form-friendly format
      const transformIssueData = (issueData) => {
        return {
          title: issueData.title || '',
          description: issueData.description || '',
          plantId: issueData.plantId || '',
          plantName: issueData.plantName || '',
          plantCapacity: issueData.plantCapacity || 0,
          category: issueData.category || '',
          issueType: issueData.issueType || '',
          priority: issueData.priority || 'Medium',
          status: issueData.status || 'Open',
          assignedTo: issueData.assignedTo || '',
          responsibleTeamExternal: issueData.responsibleTeamExternal || '',
          reportedBy: issueData.reportedBy || '',
          expectedClosingDate: issueData.expectedClosingDate
            ? dayjs(issueData.expectedClosingDate)
            : dayjs().add(7, 'day'),
          actualClosingDate: issueData.actualClosingDate
            ? dayjs(issueData.actualClosingDate)
            : null,
          firstObservedDate: issueData.firstObservedDate
            ? dayjs(issueData.firstObservedDate)
            : dayjs(),
          tags: issueData.tags || [],
          attachments: issueData.attachments || [],
          severityImpact: issueData.severityImpact || 'Medium',
          affectedEquipment: issueData.affectedEquipment || '',
          escalationLevel: issueData.escalationLevel || '',
          rootCausePreliminary: issueData.rootCausePreliminary || '',
          nextActionFollowUpPlan: issueData.nextActionFollowUpPlan || '',
          actionTaken: issueData.actionTaken || '',
          customerCommunicationDone: issueData.customerCommunicationDone || false,
        }
      }

      setFormData(transformIssueData(issue))
    }
  }, [issue])

  /*When users are loaded and an assignedTo name exists, try to find matching userId
  useEffect(() => {
    if (users.length > 0 && formData.assignedTo) {
      const matched = users.find((u) => u.name === formData.assignedTo)
      if (matched && matched.userId && matched.userId !== formData.assignedToUserId) {
        setFormData((prev) => ({ ...prev, assignedToUserId: matched.userId }))
      }
    }
  }, [users])*/

  const loadPlants = async () => {
    try {
      setLoading(true)
      console.log('fetching plants for issues (DB + hardcoded)')
      const plantList = await fetchPlantsForIssues()
      console.log('plantList in issue modal:', plantList)
      setPlants(plantList)
    } catch (error) {
      console.error('Error loading plants:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {
    try {
      const userList = await fetchUserList()
      // normalise to array
      setUsers(Array.isArray(userList) ? userList : [])
      //console.log("Users fetched (in IssueFOrm", userList)
    } catch (error) {
      console.error('Error loading users:', error)
    }
  }

  const loadFrequentTags = async () => {
    try {
      const tags = await fetchFrequentTags(8)
      if (Array.isArray(tags) && tags.length > 0) {
        setFrequentTags(tags)
      } else {
        //use hardcoded default tags if no tags returen from API
        setFrequentTags(DEFAULT_TAG_SUGGESTIONS.map((tag) => ({ tag })))
      }
    } catch (error) {
      console.error('Error loading frequent tags:', error)
      setFrequentTags(DEFAULT_TAG_SUGGESTIONS.map((tag) => ({ tag })))
    }
  }

  const handleAddFrequentTag = (tag) => {
    if (!formData.tags.includes(tag)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tag],
      }))
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    //Auto-set actualClosingDate when status is changed to Resolved
    if (field === 'status' && value === 'Resolved' && !formData.actualClosingDate) {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        actualClosingDate: dayjs(),
      }))
      return
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const handlePlantChange = (plantId) => {
    const selectedPlant = plants.find((plant) => plant.id === plantId)
    setFormData((prev) => ({
      ...prev,
      plantId,
      plantName: selectedPlant ? selectedPlant.plantName : '',
      plantCapacity: selectedPlant ? selectedPlant.plantSize : 0,
    }))
  }

  const handleUserChange = (userName) => {
    // Store the selected name directly
    setFormData((prev) => ({
      ...prev,
      assignedTo: userName || '',
      assignedToUserId: '', // Clear any previous user ID
    }))
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }))
      setTagInput('')
    }
  }

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }))
  }

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  const handleFileUpload = async (event) => {
    event.preventDefault()
    const files = Array.from(event.target.files)

    //upload files in edit mode (issue already exists)
    if (issue && issue.id) {
      setLoading(true)

      try {
        const uploadPromises = files.map(async (file) => {
          const uploadedAttachment = await uploadAttachment(issue.id, file)
          return {
            id: uploadedAttachment.id,
            name: uploadedAttachment.originalName,
            size: uploadedAttachment.size,
            type: uploadedAttachment.mimetype,
            url: uploadedAttachment.url,
            uploadDate: uploadedAttachment.createdAt,
            isUploaded: true, // Flag to indicate this is already uploaded
          }
        })

        const uploadedAttachments = await Promise.all(uploadPromises)

        setFormData((prev) => ({
          ...prev,
          attachments: [...prev.attachments, ...uploadedAttachments],
        }))
      } catch (error) {
        console.error('Error uploading attachments:', error)
      } finally {
        setLoading(false)
      }
    } else {
      // Store files locally in create mode (issue doesn't exist yet)
      const newAttachments = files.map((file) => ({
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        file: file, // Store the actual File object
        uploadDate: new Date().toISOString(),
        isUploaded: false, // Flag to indicate this needs to be uploaded
      }))

      console.log('Adding attachments to form (create mode):', newAttachments)

      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...newAttachments],
      }))
    }

    // Clear the input
    event.target.value = ''
  }

  const handleRemoveAttachment = (attachmentId) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((att) => att.id !== attachmentId),
    }))
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }

    if (!formData.plantId) {
      newErrors.plantId = 'Plant selection is required'
    }

    if (!formData.category) {
      newErrors.category = 'Category is required'
    }

    if (!formData.issueType) {
      newErrors.issueType = 'Issue Type is required'
    }

    if (!formData.priority) {
      newErrors.priority = 'Priority is required'
    }

    if (!formData.escalationLevel) {
      newErrors.escalationLevel = 'Escalation Level is required'
    }

    //validate actualClosingDate if provided
    if (formData.actualClosingDate) {
      if (formData.firstObservedDate && formData.actualClosingDate.isBefore(formData.firstObservedDate)) {
        newErrors.actualClosingDate = 'Actual closing date cannot be before first observed date'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      const {
        attachments, //exclude attachments as it uses a different backend model
        actualClosingDate, // extract this separately to handle null case
        ...issueFormData
      } = formData

      // ensure we only send assignedTo (name) to backend - strip assignedToUserId
      const { assignedToUserId, ...issueFormDataWithoutUserId } = issueFormData

      const submitData = {
        ...issueFormDataWithoutUserId,
        expectedClosingDate: formData.expectedClosingDate.toISOString(),
        firstObservedDate: formData.firstObservedDate.toISOString(),
        plantName: formData.plantName,
        plantCapacity: formData.plantCapacity,
      }

      //Handle actualClosingDate: include it if it has a value, or explicitly set to null if cleared
      if (actualClosingDate) {
        submitData.actualClosingDate = actualClosingDate.toISOString()
      } else if (issue && actualClosingDate === null) {
        submitData.actualClosingDate = null
      }

      // Automatically set actualClosingDate when status is Closed
      if ((formData.status === 'Closed')) {
        submitData.actualClosingDate = new Date().toISOString()
      }

      await onSubmit(submitData, attachments || [])
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Issue Title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            multiline
            rows={4}
            error={!!errors.description}
            helperText={errors.description}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            fullWidth
            disablePortal
            PopperProps={{
              placement: 'bottom-start',
              modifiers: [
                {
                  name: 'flip',
                  enabled: false,
                },
              ],
            }}
            loading={loading}
            options={plants}
            getOptionLabel={(option) =>
              option.projectCode
                ? `${option.plantName} - ${option.projectCode}`
                : option.plantName
            }
            value={
              plants.find((p) => p._id === formData.plantId) ||
              (formData.plantId ? {
                _id: formData.plantId,
                plantName: formData.plantName || 'Unknown Plant',
                projectCode: null,
                plantSize: formData.plantCapacity || 0,
              } : null)
            }
            onChange={(e, newValue) =>
              handlePlantChange(newValue ? newValue.id : '')
            }
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Plant"
                required
                error={!!errors.plantId}
                helperText={errors.plantId}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              loading ? 'Fetching plants...' : 'No plants found.'
            }
            filterOptions={(options, { inputValue }) =>
              options.filter((plant) =>
                `${plant.plantName} ${plant.projectCode}`
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              )
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.category} required>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              label="Category"
            >
              {categoryOptions.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
            {errors.category && (
              <FormHelperText>{errors.category}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.issueType} required>
            <InputLabel>Issue Type</InputLabel>
            <Select
              value={formData.issueType}
              onChange={(e) => handleInputChange('issueType', e.target.value)}
              label="Issue Type"
            >
              {issueTypeOptions?.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {errors.issueType && (
              <FormHelperText>{errors.issueType}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.escalationLevel} required>
            <InputLabel>Escalation Level</InputLabel>
            <Select
              value={formData.escalationLevel}
              onChange={(e) =>
                handleInputChange('escalationLevel', e.target.value)
              }
              label="Escalation Level"
            >
              {escalationOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {errors.escalationLevel && (
              <FormHelperText>{errors.escalationLevel}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', e.target.value)}
              label="Priority"
            >
              {priorityOptions.map((priority) => (
                <MenuItem key={priority} value={priority}>
                  {priority}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              label="Status"
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Severity Impact</InputLabel>
            <Select
              value={formData.severityImpact}
              onChange={(e) =>
                handleInputChange('severityImpact', e.target.value)
              }
              label="Severity Impact"
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Assign To</InputLabel>
            <Select
              value={formData.assignedTo || ''}
              onChange={(e) => handleUserChange(e.target.value)}
              label="Assign To"
            >
              <MenuItem value="">Unassigned</MenuItem>
              {assignedToOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Assign To (External)"
            value={formData.responsibleTeamExternal}
            onChange={(e) => handleInputChange('responsibleTeamExternal', e.target.value)}
            inputProps={{
              maxLength: 50,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Reported By</InputLabel>
            <Select
              value={formData.reportedBy || ''}
              onChange={(e) => handleInputChange('reportedBy', e.target.value)}
              label="Reported By"
            >
              <MenuItem value="Coordinator">Coordinator</MenuItem>
              <MenuItem value="Engineer">Engineer</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="First Observed Date"
              format="DD-MM-YYYY HH:mm"
              value={formData.firstObservedDate}
              onChange={(date) =>
                handleInputChange('firstObservedDate', date)
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expected Closing Date"
              format="DD-MM-YYYY"
              value={formData.expectedClosingDate}
              onChange={(date) =>
                handleInputChange('expectedClosingDate', date)
              }
              slotProps={{ textField: { fullWidth: true } }}
              minDate={dayjs()}
            />
          </LocalizationProvider>
        </Grid>

        {issue && (
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Actual Closure Date"
                format="DD-MM-YYYY"
                value={formData.actualClosingDate}
                onChange={(date) =>
                  handleInputChange('actualClosingDate', date)
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.actualClosingDate,
                    helperText: errors.actualClosingDate
                  },
                  field: { clearable: true }
                }}
                minDate={formData.firstObservedDate || dayjs().subtract(1, 'year')}
              />
            </LocalizationProvider>
          </Grid>
        )}

        {/* Additional Information */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Affected Equipment"
            value={formData.affectedEquipment}
            onChange={(e) =>
              handleInputChange('affectedEquipment', e.target.value)
            }
            placeholder="e.g., Inverter 1, Energy Meter 2, etc."
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Root Cause (Preliminary)"
            value={formData.rootCausePreliminary}
            onChange={(e) =>
              handleInputChange('rootCausePreliminary', e.target.value)
            }
            multiline
            rows={3}
            placeholder="Describe the preliminary root cause analysis"
          />
        </Grid>

        {issue && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Remark / Action Taken"
              value={formData.actionTaken}
              onChange={(e) =>
                handleInputChange('actionTaken', e.target.value)
              }
              multiline
              rows={3}
              placeholder="Describe actions taken to address the issue"
            />
          </Grid>
        )}

        {issue && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Action Plan / Next Follow-up"
              value={formData.nextActionFollowUpPlan}
              onChange={(e) =>
                handleInputChange('nextActionFollowUpPlan', e.target.value)
              }
              multiline
              rows={3}
              placeholder="Describe the action plan and next steps"
            />
          </Grid>
        )}

        {issue && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.customerCommunicationDone}
                  onChange={(e) =>
                    handleInputChange('customerCommunicationDone', e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Customer Communication Done"
            />
          </Grid>
        )}

        {/* Tags */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Tags
          </Typography>

          {frequentTags.filter((item) => !formData.tags.includes(item.tag)).slice(0, 4).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                Frequently used:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {frequentTags
                  .filter((item) => !formData.tags.includes(item.tag))
                  .slice(0, 4)
                  .map((item) => (
                    <Chip
                      key={item.tag}
                      label={item.tag}
                      size="small"
                      variant="outlined"
                      onClick={() => handleAddFrequentTag(item.tag)}
                      sx={{
                        cursor: 'pointer',
                        borderColor: '#5968b7',
                        color: '#5968b7',
                        '&:hover': {
                          backgroundColor: 'rgba(89, 104, 183, 0.08)',
                          borderColor: '#5968b7',
                        },
                      }}
                    />
                  ))}
              </Box>
            </Box>
          )}

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <TextField
              size="small"
              placeholder="Add tag"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleTagKeyPress}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddTag}
              disabled={!tagInput.trim()}
            >
              Add
            </Button>
          </Box>

          {/* Selected Tags */}
          <Box display="flex" flexWrap="wrap" gap={1} sx={{ minHeight: '40px' }}>
            {formData.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                size="small"
                sx={{
                  backgroundColor: '#5968b7',
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'white',
                    '&:hover': {
                      color: '#f0f0f0',
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Grid>

        {/* File Attachments */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Attachments
          </Typography>
          <Box mb={2}>
            <input
              accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
              style={{ display: 'none' }}
              id="file-upload"
              multiple
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                size="small"
              >
                Upload Files
              </Button>
            </label>
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              mt={0.5}
            >
              Supported: Images, PDF, Word docs, Text files, CSV, Excel (Max
              10MB each)
            </Typography>
          </Box>

          {formData.attachments.length > 0 && (
            <List dense>
              {formData.attachments.map((attachment) => (
                <ListItem key={attachment.id} divider>
                  <AttachFileIcon color="action" sx={{ mr: 1 }} />
                  <ListItemText
                    primary={attachment.name}
                    secondary={`${formatFileSize(attachment.size)} • ${new Date(
                      attachment.uploadDate
                    ).toLocaleDateString()}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleRemoveAttachment(attachment.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Grid>

        {/* Form Actions */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button variant="outlined" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : issue ? 'Update Issue' : 'Create Issue'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default IssueForm